import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { ListOrdered } from "lucide-react";
import { ContentReactions, ReportLink, Comments, ListBody, when } from "@/components/ContentBits";

/** /list/:id - one list. Anyone with the link sees public lists; friends also see friends-only lists. */
export default function ListPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setList(null); setMissing(false);
    api.get(`/public/lists/${encodeURIComponent(id)}`).then((r) => setList(r.data)).catch(() => setMissing(true));
  }, [id, user?.id]);

  if (missing) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <Seo title="List not found" noindex />
        <ListOrdered className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-4 font-serif text-3xl font-semibold">This list isn't available</h1>
        <p className="mt-2 text-muted-foreground">It may be private, or the link may be wrong.</p>
        <Link to="/" className="mt-6 inline-flex rounded-full bg-[#C89B3C] px-6 py-2.5 text-sm font-semibold text-black">Go to My Theatre</Link>
      </div>
    );
  }
  if (!list) return <p className="px-4 py-20 text-center text-muted-foreground">Loading…</p>;

  const canReact = list.viewer === "friend";
  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <Seo title={list.title} noindex />
      {!user && (
        <div className="mb-6 rounded-2xl border border-[#C89B3C]/40 bg-gradient-to-r from-[#1a1218] via-[#2a1620] to-[#1a1218] p-5 text-center" data-testid="join-banner">
          <p className="font-serif text-xl font-semibold text-amber-50">Make your own theatre lists</p>
          <p className="text-sm text-amber-100/70">Free: keep a watchlist and diary, and share lists with friends.</p>
          <Link to="/login" className="mt-3 inline-flex rounded-full bg-[#C89B3C] px-6 py-2.5 text-sm font-semibold text-black">Join My Theatre</Link>
        </div>
      )}
      <p className="overline-label text-[#C89B3C]">A theatre list</p>
      <h1 className="mt-1 font-serif text-3xl font-semibold leading-tight sm:text-4xl" data-testid="list-page-title">{list.title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        by {list.username ? <Link to={`/u/${list.username}`} className="underline">{list.username}</Link> : "a theatre fan"} · {when(list.updated_at)}
        {list.viewer === "owner" && <span className="ml-2 rounded bg-[#C89B3C]/20 px-1.5 py-0.5 text-[#E6C071]">Your list</span>}
      </p>
      {list.description && <p className="mt-2 text-muted-foreground">{list.description}</p>}
      <ListBody list={list} />
      {list.viewer === "friend" && <ContentReactions kind="list" item={list} canReact={canReact} onChange={setList} />}
      {list.viewer === "owner" && <ContentReactions kind="list" item={list} canReact={false} onChange={setList} />}
      {(list.viewer === "friend" || list.viewer === "owner") && list.visibility !== "private" && (
        <Comments kind="list" id={list.id} count={list.comment_count || 0} onCount={(n) => n !== (list.comment_count || 0) && setList((l) => ({ ...l, comment_count: n }))} />
      )}
      {user && list.viewer !== "owner" && <div className="mt-6 text-center"><ReportLink kind="list" id={list.id} /></div>}
      {!user && <p className="mt-8 text-center text-xs text-muted-foreground">Something wrong with this page? <a className="underline" href={`mailto:hello@mytheatre.uk?subject=Report list ${encodeURIComponent(list.id)}`}>Report it</a></p>}
    </div>
  );
}